import { Button, Grid2, Stack } from "@mui/material";
import ContainerLoading from "../components/ContainerLoading";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useFormik } from "formik";
import { Input } from "../components/Input";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";

import {
  formatCpfCnpj,
  formatPhone,
  phoneValidate,
  unMask,
} from "../helpers/format";
import PageHeader from "../components/PageHeader";
import moment from "moment";
import { professorsListById, professorUpdate } from "../services/professors";
import { cpf } from "cpf-cnpj-validator";
import ProfessorDelete from "../components/ProfessorDelete";

const ProfessorDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [deleteAlert, setDeleteAlert] = useState(false);

  const {
    data,
    isLoading,
    isFetching,
    refetch,
  }: {
    data: {
      id: string | number;
      fullName: string;
      email: string;
      phone: string;
      createdAt: string | Date;
      status: string;
      birthDate: Date;
      document: string;
      nickName: string;
    };
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
  } = useQuery({
    queryKey: ["professorsListById" + id],
    queryFn: async () => await professorsListById(Number(id)),
    initialData: [],
  });

  const { mutate, isPending } = useMutation({
    mutationFn: professorUpdate,
    onSuccess: () => {
      refetch();
      toast.success("Professor atualizado");
    },

    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(String(e?.response?.data?.message) || "Erro!");
    },
  });

  const { values, handleSubmit, handleChange, setFieldValue } = useFormik({
    initialValues: {
      id: "",
      fullName: "",
      email: "",
      phone: "",
      birthDate: "",
      nickName: "",
      document: "",
      description: "",
    },
    onSubmit: (values) => {
      const requiredFields = [
        { field: values.fullName, label: "Nome" },
        { field: values.document, label: "CPF" },
        { field: values.phone, label: "Telefone" },
      ];

      for (const item of requiredFields) {
        if (!item.field || item.field.trim() === "") {
          toast.error(`Preencha o campo "${item.label}"`);
          return;
        }
      }
      if (!cpf.isValid(unMask(values.document))) {
        toast.error("CPF inválido");
        return;
      }
      if (!phoneValidate(formatPhone(values.phone))) {
        toast.error("Telefone inválido");
        return;
      }
      mutate({
        id: values.id,
        fullName: values?.fullName,
        nickName: values?.nickName,
        email: values.email,
        phone: unMask(values.phone),
        document: unMask(values.document),
        birthDate: moment(values.birthDate).toDate(),
      });
    },
  });

  useEffect(() => {
    if (data) {
      setFieldValue("id", data.id);
      setFieldValue("fullName", data?.fullName ?? "");
      setFieldValue("nickName", data?.nickName ?? "");
      setFieldValue(
        "birthDate",
        moment(data?.birthDate).format("YYYY-MM-DD").toString() ?? ""
      );
      setFieldValue("email", data?.email ?? "");
      setFieldValue("phone", data?.phone ?? "");
      setFieldValue("document", data?.document ?? "");
      setFieldValue("nickName", data?.nickName ?? "");
    }
  }, [data]);

  const loading = isPending || isLoading || isFetching;

  return (
    <ContainerLoading loading={loading}>
      <Grid2 container spacing={2} padding={2}>
        <PageHeader
          title="Detalhes do professor"
          subtitle="Informações gerais do professor."
          back
        />
        <Grid2 size={12}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Input
                id="fullName"
                name="fullName"
                label="Nome"
                placeholder="Digite o nome"
                type="text"
                fullWidth
                size="small"
                value={values.fullName}
                onChange={handleChange}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Input
                id="nickName"
                name="nickName"
                label="Apelido (Opcional)"
                placeholder="Digite o apelido"
                type="text"
                fullWidth
                size="small"
                value={values.nickName}
                onChange={handleChange}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <Input
                id="birthDate"
                name="birthDate"
                label="Nascimento"
                type="date"
                fullWidth
                value={values.birthDate}
                onChange={handleChange}
                onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                  e.target.showPicker()
                }
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Input
                id="document"
                name="document"
                label="CPF"
                placeholder="Digite o CPF"
                type="text"
                fullWidth
                size="small"
                value={formatCpfCnpj(values.document)}
                onChange={handleChange}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Input
                id="phone"
                name="phone"
                label="Telefone"
                placeholder="Digite o telefone"
                type="text"
                fullWidth
                size="small"
                value={formatPhone(values.phone)}
                onChange={handleChange}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Input
                id="email"
                name="email"
                label="E-mail"
                placeholder="Digite o e-mail"
                type="text"
                fullWidth
                size="small"
                value={values.email}
                onChange={handleChange}
              />
            </Grid2>
            <Grid2 size={12}>
              <Stack
                sx={{
                  flexDirection: "row",
                  alignItems: "flex-end",
                  width: "100%",
                  justifyContent: "flex-end",
                  gap: 1,
                }}
              >
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setDeleteAlert(true)}
                >
                  Excluir
                </Button>
                <Button variant="contained" onClick={() => handleSubmit()}>
                  Atualizar
                </Button>
              </Stack>
            </Grid2>
          </Grid2>
        </Grid2>
      </Grid2>
      <ProfessorDelete
        open={deleteAlert}
        onClose={() => setDeleteAlert(false)}
        id={Number(id)}
        refetch={() => navigate("/professors")}
      />
    </ContainerLoading>
  );
};

export default ProfessorDetails;
